import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Mail, Clock, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface EmailVerificationStepProps {
  email: string;
  onResend: () => void;
  onChangeEmail: () => void;
  onVerified: () => void;
  emailSentAt?: number;
  isLoading: boolean;
}

export const EmailVerificationStep: React.FC<EmailVerificationStepProps> = ({
  email,
  onResend,
  onChangeEmail,
  onVerified,
  emailSentAt,
  isLoading
}) => {
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    // Listen for auth state changes to detect when email is verified
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session?.user?.email_confirmed_at) {
          onVerified();
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [onVerified]);

  useEffect(() => {
    if (emailSentAt) {
      // Calculate backoff time based on verification attempts (passed via props would be better)
      const baseTime = 30;
      const backoffMultiplier = 1; // Could be passed as prop for exponential backoff
      const waitTime = baseTime * backoffMultiplier;
      
      setResendTimer(waitTime);
      setCanResend(false);
      
      const timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [emailSentAt]);

  return (
    <div className="space-y-6 text-center">
      <div className="space-y-4">
        <div className="mx-auto bg-primary/10 p-4 rounded-full w-fit">
          <Mail className="h-8 w-8 text-primary" />
        </div>
        
        <div className="space-y-2">
          <h3 className="text-xl font-semibold">Verify your email</h3>
          <p className="text-muted-foreground">
            We sent a verification link to <strong>{email}</strong>. 
            Click it to continue creating your account.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <Button
          onClick={onResend}
          disabled={!canResend || isLoading}
          variant="outline"
          className="w-full"
        >
          {isLoading ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : canResend ? (
            'Resend email'
          ) : (
            <>
              <Clock className="h-4 w-4 mr-2" />
              Resend in {resendTimer}s
            </>
          )}
        </Button>

        <Button
          onClick={onChangeEmail}
          variant="ghost"
          className="w-full"
        >
          Change email address
        </Button>
      </div>

      <div className="pt-4 border-t border-border">
        <div className="space-y-2 text-sm text-muted-foreground">
          <p className="font-medium">Having trouble?</p>
          <ul className="space-y-1">
            <li>• Check your spam or promotions folder</li>
            <li>• Make sure {email} is correct</li>
            <li>• Try resending after the timer expires</li>
          </ul>
        </div>
      </div>
    </div>
  );
};